// Rush logo printing and engraving on items the client already bought (not
// live). The hub the "rush logo printing" sections on the UV and 3D signs
// pages link to. Targets (coordinator research, 2026-11-03): custom tumbler
// with logo 720, custom hand fan printing 170, rush promotional products 90,
// same day engraving nyc 90, same day promotional products, logo printing on
// promotional items, uv printing on products. Prices and examples: owner
// 2026-11-03, consistent with printaworld-feed agent/PRICING.md. Same-day
// completion is not promised (.seo/truth.md laser-engraving turnaround).
const { images } = require('../engraving-services')
const {
  uvPrintingPricing,
  uvPrintingProductionRanges,
} = require('../uv-printing-pricing')
const { laserEngravingFacts } = require('../../../lib/laser-engraving-facts')
const { locations } = require('../../../lib/locations')

// The sitewide ranges (owner 2026-11-03): one source for every page.
const rushPricing = {
  uvSetup: uvPrintingPricing.setupFee,
  uvPiece: uvPrintingProductionRanges[0].price.replace(' each', ''),
  laserSetup: laserEngravingFacts.setupRange,
  laserPiece: laserEngravingFacts.pieceRange.replace(' per piece', ''),
  rush: '+50%',
}

const rushAnswer = `UV printing on your items is a ${rushPricing.uvSetup} setup and jig plus ${rushPricing.uvPiece} per piece; laser engraving is a ${rushPricing.laserSetup} setup plus ${rushPricing.laserPiece} per piece. Rush adds 50%. How fast a run finishes depends on how many items fit in each jig load.`

const rushLogoPrinting = {
  key: 'rushLogoPrinting',
  path: '/services/uv-printing/rush-logo-printing/',
  reviewedDate: '2026-11-03',
  reviewedDateLabel: 'November 3, 2026',
  reviewedNote: 'Prices from PrintAWorld’s 2026 UV and engraving quotes',
  title: 'Rush Logo Printing & Engraving on Items You Supply | NYC',
  description:
    'Rush logo printing and engraving on promo items you already bought: tumblers, mugs, fans, power banks. NYC drop-off. UV $150–$250 setup + $20–$30/pc.',
  crumbs: [['UV printing', '/uv-printing-services/']],
  crumb: 'Rush logo printing',
  eyebrow: 'Rush logo printing / your items, our NYC studio',
  heading:
    'Rush logo printing and engraving on promotional items you already bought.',
  answer: `Many print shops won’t touch items you bring them. We will. PrintAWorld UV prints full-color logos and laser engraves names on client-supplied promo items in our New York studio: custom tumblers with your logo, mugs, folding fans, power banks, clipboards and more, for activations, events and PR mailers. ${rushAnswer} Drop off in Brooklyn or Queens, or send a courier.`,
  cta: 'Get a rush quote',
  heroPhoto: {
    url: images.coasters.url,
    alt: images.coasters.alt,
    caption: 'Logos engraved on finished items',
  },
  keyFacts: [
    {
      label: 'UV printing',
      value: `${rushPricing.uvPiece} / pc`,
      note: `Plus ${rushPricing.uvSetup} setup and jig`,
    },
    {
      label: 'Engraving',
      value: `${rushPricing.laserPiece} / pc`,
      note: `Plus ${rushPricing.laserSetup} setup`,
    },
    { label: 'Rush', value: '+50%', note: 'Overnight on some jobs' },
    { label: 'Your items', value: 'Welcome', note: 'Send spares to test' },
  ],
  sections: [
    {
      type: 'cards',
      id: 'work',
      eyebrow: 'Real rush jobs',
      title: 'Logos and names on items clients brought us.',
      intro: 'Described without the brand.',
      index: '01 / OUR WORK',
      items: [
        {
          title: '96 ceramic mugs, overnight',
          body: 'A full-color logo on 96 ceramic mugs, printed overnight for an activation in Tribeca.',
        },
        {
          title: '55 iced tumblers',
          body: 'A logo on 55 iced-coffee tumblers for a beauty and coffee collaboration.',
        },
        {
          title: '43 hairbrushes',
          body: 'A guest name on each of 43 hairbrushes for an experiential agency.',
        },
        {
          title: '40 folding fans',
          body: 'Logos printed on 40 folding hand fans for an event.',
        },
        {
          title: '500 tape dispensers',
          body: 'Logo decals on 500 tape dispensers.',
        },
        {
          title: '250 aluminum clipboards',
          body: 'Engraved aluminum clipboards for a running brand’s event.',
        },
        {
          title: 'Power banks',
          body: 'A logo printed on power banks for a corporate giveaway.',
        },
        {
          title: 'Cocktail stencils, next day',
          body: 'Acrylic logo stencils for cocktails, laser cut next day.',
          link: ['Laser cutting', '/services/laser-cutting/'],
        },
      ],
    },
    {
      type: 'cards',
      id: 'which',
      eyebrow: 'UV or engraving',
      title: 'Which one your item needs.',
      index: '02 / THE METHOD',
      items: [
        {
          label: 'UV printing',
          title: 'Full color on mugs, fans, plastic and boxes',
          body: 'Your logo in color or white ink, printed onto the item, or as a hand-applied UV transfer when the shape won’t fit the printer.',
          link: ['UV printing', '/uv-printing-services/'],
        },
        {
          label: 'Laser engraving',
          title: 'Tumblers, metal, wood and leather',
          body: 'A permanent mark: powder-coated tumblers engrave to bright steel, aluminum and wood to a tone-on-tone mark.',
          link: ['Laser engraving in NYC', '/laser-engraving-service-nyc/'],
        },
        {
          label: 'Not sure',
          title: 'Send a photo',
          body: 'Tell us the item, the quantity and the date. We’ll tell you which method works and test it on a spare.',
        },
      ],
    },
    {
      type: 'pricing',
      id: 'pricing',
      eyebrow: 'Pricing',
      title: 'Setup plus a price per piece. Rush adds 50%.',
      index: '03 / THE PRICE',
      lead: {
        label: 'UV setup and jig',
        value: rushPricing.uvSetup,
        note: 'Once per job, plus per-piece printing.',
      },
      rowsTitle: 'Per piece',
      rows: [
        { label: 'UV printing', value: `${rushPricing.uvPiece} / pc` },
        { label: 'Laser engraving setup', value: rushPricing.laserSetup },
        { label: 'Laser engraving', value: `${rushPricing.laserPiece} / pc` },
        { label: 'Rush', value: rushPricing.rush },
        { label: 'Taxes and shipping', value: 'Not included' },
      ],
      notes: [
        'How many items fit in one jig load sets how fast a run goes: flat items load many at a time, mugs and tumblers one or a few. We confirm the finish time when we see the item.',
      ],
      cta: 'Price my items',
    },
    {
      type: 'steps',
      id: 'process',
      eyebrow: 'How a rush job works',
      title: 'Drop off today, pick up when it’s done.',
      intro:
        'We test on a spare before the run, every time. Same-day completion isn’t guaranteed; overnight and next-day jobs depend on the item and the queue.',
      index: '04 / THE PROCESS',
      items: [
        {
          title: 'Send a photo and the date',
          body: 'The item, how many, the logo file and when you need them.',
        },
        {
          title: 'Pay to start',
          body: 'Rush starts when the invoice is paid, so we can hold the machine time.',
        },
        {
          title: 'Drop off with spares',
          body: `At ${locations.map((location) => location.address.replace(/, NY \d+$/, '')).join(' or ')}, by appointment. An Uber courier is fine.`,
        },
        {
          title: 'Test, run, pick up',
          body: 'We test a spare, send a photo, run the batch and tell you when it’s ready.',
        },
      ],
    },
    {
      type: 'contrast',
      id: 'agencies',
      eyebrow: 'For agencies and event teams',
      title: 'Bought the merch already? Bring it to us.',
      paragraphs: [
        'Most promo printers sell you their own blanks and won’t print on items you bought elsewhere. We print and engrave on yours, with spares for testing.',
        'Need it on site instead? We run live engraving stations at New York events.',
      ],
      cta: 'Plan a rush job',
    },
  ],
  reviews: 'uv-printing',
  faqTitle: 'Rush logo printing questions, answered.',
  faqs: [
    {
      question: 'Can you print our logo on items we already bought?',
      answer:
        'Yes. We UV print and laser engrave on client-supplied items. Send 2–3 spares so we can test the ink or the engraving first.',
    },
    {
      question: 'How much does rush logo printing cost?',
      answer: `${rushAnswer} Taxes and shipping aren’t included.`,
    },
    {
      question: 'Can you do same-day engraving in NYC?',
      answer:
        'Sometimes, for a small run when the items and artwork arrive early, but we don’t guarantee same day. Overnight and next-day jobs are more common: 96 ceramic mugs were printed overnight. Tell us your deadline first.',
    },
    {
      question: 'Can you put a logo on custom tumblers?',
      answer:
        'Yes. Powder-coated tumblers engrave to bright steel; plastic and clear tumblers can be UV printed in color. We printed a logo on 55 iced tumblers for a beauty and coffee collaboration.',
    },
    {
      question: 'Do you print on folding hand fans?',
      answer:
        'Yes. We printed logos on 40 folding fans for an event. Send one fan so we can check the material and the print area.',
    },
    {
      question: 'Where do we drop off the items?',
      answer:
        'At our Brooklyn studio (616 Berriman Street) or our Glendale, Queens studio (74-12 88th Street), by appointment. An Uber courier or other courier is fine.',
    },
    {
      question: 'Can you print metallic gold or silver?',
      answer:
        'No metallic ink; the closest is silver-gray. For a metal look, engraving a metal or coated item usually works better.',
    },
  ],
  quote: {
    eyebrow: 'Start your rush job',
    title: 'What are we printing?',
    body: 'Send a photo of the item, the quantity, your logo and the deadline. We’ll reply with the method, the price and when it can be ready.',
    checklist: [
      'A photo or link to the item',
      'How many, plus 2–3 spares',
      'Your logo file and where it goes',
      'The date you need them',
    ],
  },
  eventPlanner: false,
  // Studio planner preset (src/data/engraving-planner.js).
  plannerPreset: 'rushPrint',
  related: [
    ['UV printing', '/uv-printing-services/'],
    ['UV printing in NYC', '/services/uv-printing/ny/new-york/'],
    ['Laser engraving in NYC', '/laser-engraving-service-nyc/'],
    [
      'Beauty products for PR mailers',
      '/services/laser-engraving/beauty-products/',
    ],
  ],
  schema: {
    name: 'Rush logo printing and engraving on client-supplied items',
    alternateName: [
      'Rush promotional products',
      'Logo printing on promotional items',
      'Custom tumblers with logo',
      'Same-day engraving NYC',
    ],
    serviceType: 'UV printing and laser engraving',
    areaServed: { '@type': 'City', name: 'New York City' },
    minPrice: 75,
  },
}

module.exports = { rushLogoPrinting, rushPricing, rushAnswer }
